
import { supabase } from "@/integrations/supabase/client";
import { ShoppingList } from "@/types";
import { mapItemFromDatabase, mapListFromDatabase } from "./utils";
import { getCachedUserId, setCachedUser } from "@/services/auth/sessionCache";

// Function to get all shopping lists
export const getShoppingLists = async (): Promise<ShoppingList[]> => {
  try {
    const userId = await getCachedUserId();
    if (!userId) {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        console.error('User must be authenticated');
        return [];
      }
      setCachedUser(userData.user);
    }
    const finalUserId = userId || (await getCachedUserId());

    const { data: listsData, error } = await supabase
      .from('shopping_lists')
      .select('*')
      .eq('user_id', finalUserId!)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error retrieving lists:', error);
      return [];
    }

    // For each list, get its items
    const lists: ShoppingList[] = [];
    
    for (const list of listsData || []) {
      try {
        const { data: itemsData, error: itemsError } = await supabase
          .from('shopping_items')
          .select('*')
          .eq('list_id', list.id)
          .eq('user_id', finalUserId!)
          .order('created_at', { ascending: false });
        
        if (itemsError) {
          console.error(`Error retrieving items for list ${list.id}:`, itemsError);
          continue;
        }

        lists.push({
          ...mapListFromDatabase(list),
          items: itemsData ? itemsData.map(item => mapItemFromDatabase(item)) : []
        });
      } catch (err) {
        console.error(`Error processing list ${list.id}:`, err);
      }
    }

    return lists;
  } catch (err) {
    console.error('General error retrieving lists:', err);
    return [];
  }
};

export const updateRapidInsertFlag = async (listId: string, rapid: boolean) => {
  const { error } = await supabase
    .from("shopping_lists")
    .update({ rapid_insert: rapid })
    .eq("id", listId);

  if (error) {
    console.error("Errore aggiornando rapid_insert:", error);
  } else {
    console.info("🟢 rapid_insert aggiornato a", rapid);
  }
};

// Function to get a single shopping list
export const getShoppingList = async (listId: string): Promise<ShoppingList | null> => {
  try {
    const userId = await getCachedUserId();
    let finalUserId = userId;
    if (!finalUserId) {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        console.error('User must be authenticated');
        return null;
      }
      setCachedUser(userData.user);
      finalUserId = userData.user.id;
    }

    const { data: list, error } = await supabase
      .from('shopping_lists')
      .select('*')
      .eq('id', listId)
      .eq('user_id', finalUserId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error(`Error retrieving list ${listId}:`, error);
      return null;
    }
    if (!list) {
      console.warn(`⚠️ List ${listId} not found`);
      return null;
    }

    const { data: items, error: itemsError } = await supabase
      .from('shopping_items')
      .select('*')
      .eq('list_id', listId)
      .eq('user_id', finalUserId)
      .order('position', { ascending: true, nullsFirst: true });
    
    if (itemsError) {
      console.error(`Error retrieving items for list ${listId}:`, itemsError);
      return null;
    }

    return {
      ...mapListFromDatabase(list),
      items: items ? items.map(item => mapItemFromDatabase(item)) : []
    };
  } catch (err) {
    console.error(`General error retrieving list ${listId}:`, err);
    return null;
  }
};

// Function to update a list's background
export const updateListBackground = async (listId: string, backgroundGradient: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('shopping_lists')
      .update({ 
        background_gradient: backgroundGradient,
        updated_at: new Date().toISOString()
      })
      .eq('id', listId);

    if (error) {
      console.error(`Error updating list background ${listId}:`, error);
      return false;
    }

    return true;
  } catch (err) {
    console.error(`General error updating list background ${listId}:`, err);
    return false;
  }
};

// Function to create a new shopping list
export const createShoppingList = async (name: string): Promise<ShoppingList | null> => {
  try {
    const userId = await getCachedUserId();
    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return null;
      }
      setCachedUser(userData.user);
    }
    const finalUserId = userId || (await getCachedUserId());

    // Create the list
    const { data, error } = await supabase
      .from('shopping_lists')
      .insert([
        {
          name,
          user_id: finalUserId
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating list:', error);
      return null;
    }

    return {
      ...mapListFromDatabase(data),
      items: []
    };
  } catch (err) {
    console.error('General error creating list:', err);
    return null;
  }
};

// Function to delete a shopping list
export const deleteShoppingList = async (listId: string): Promise<boolean> => {
  try {
    // Delete the list (cascade delete should handle the items)
    const { error } = await supabase
      .from('shopping_lists')
      .delete()
      .eq('id', listId);

    if (error) {
      console.error(`Error deleting list ${listId}:`, error);
      return false;
    }

    return true;
  } catch (err) {
    console.error(`General error deleting list ${listId}:`, err);
    return false;
  }
};

// Function to set a list as primary
export const setPrimaryList = async (listId: string): Promise<boolean> => {
  try {
    const userId = await getCachedUserId();
    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('No authenticated user found');
      }
      setCachedUser(userData.user);
    }
    const finalUserId = userId || (await getCachedUserId());

    // Prima, reimpostiamo tutte le liste di proprietà dell'utente come non primarie
    const { error: clearError } = await supabase
      .from('shopping_lists')
      .update({ is_primary: false })
      .eq('user_id', finalUserId!);

    if (clearError) {
      console.error('Error unsetting primary lists:', clearError);
      return false;
    }

    // Quindi impostiamo la lista selezionata come primaria
    const { error } = await supabase
      .from('shopping_lists')
      .update({ is_primary: true })
      .eq('id', listId);

    if (error) {
      console.error(`Error setting list ${listId} as primary:`, error);
      return false;
    }

    return true;
  } catch (err) {
    console.error(`General error setting list ${listId} as primary:`, err);
    return false;
  }
};
